import { Check, Download, RotateCcw, Share2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useGame } from '../game/GameContext';
import { loadHistory } from '../game/storage';
import { createShareCard, downloadShareCard, shareResult, type ShareCardVariant } from '../share/renderShareCard';
import { BrandMark } from './BrandMark';

export function AftermathScreen() {
  const { state, dispatch } = useGame();
  const [shared, setShared] = useState(false);
  const [variant, setVariant] = useState<ShareCardVariant>('verdict');
  const [preview, setPreview] = useState<string | null>(null);
  const outcome = state.outcome;
  useEffect(() => {
    if (!outcome) return;
    let active = true;
    let url: string | null = null;
    void createShareCard(outcome, state.profile, variant).then((blob) => {
      if (!active) return;
      url = URL.createObjectURL(blob);
      setPreview(url);
    });
    return () => {
      active = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [outcome, state.profile, variant]);
  if (!outcome) return null;
  const history = loadHistory();

  const share = async () => {
    await shareResult(outcome, state.profile, variant);
    setShared(true);
    window.setTimeout(() => setShared(false), 2400);
  };

  return (
    <main className="aftermath screen-layer">
      <header><BrandMark compact /><span>CASE RECORD / CLOSED</span></header>
      <section className="verdict-card">
        <div className="verdict-card__copy">
          <p className="eyebrow">Tribunal finding</p>
          <h1>{outcome.verdict}</h1>
          <p>{outcome.truthFound ? 'You found the world truth and made it survive contact with the law.' : 'You protected the client, but part of Archive Nine still belongs to the dark.'}</p>
          <div className="verdict-card__route"><span>COUNSEL ROUTE</span><strong>{outcome.routeTitle}</strong><small>+{outcome.xpAwarded} career XP · Level {state.profile.level}</small></div>
          <div className="verdict-card__stats">
            <div><span>Rank</span><strong>{outcome.rank}</strong></div>
            <div><span>Case score</span><strong>{outcome.score}<small>/100</small></strong></div>
            <div><span>Truth</span><strong>{outcome.truthFound ? 'Recovered' : 'Partial'}</strong></div>
          </div>
          <div className="result-hash"><span>VERIFIABLE COUNSEL RECORD</span><strong>{outcome.resultHash}</strong><small>Yuragi genesis replay {outcome.yuragiReplayVerified ? 'verified' : 'unverified'} · herd sync {Math.round(outcome.yuragiSyncOrder * 100)}%</small><code>{outcome.yuragiProofHash}</code></div>
          <div className="verdict-card__actions">
            <button className="primary-action" onClick={share}>{shared ? <Check /> : <Share2 />}{shared ? 'Result copied' : 'Share result'}</button>
            <button className="secondary-action" onClick={() => void downloadShareCard(outcome, state.profile, variant)}><Download /> Download card</button>
            <button className="text-action" onClick={() => dispatch({ type: 'RESET_CASE' })}><RotateCcw /> New record</button>
          </div>
        </div>
        <div className="verdict-card__visual share-studio">
          <header><span>SHARE CARD STUDIO</span><small>1200 × 630 · PNG</small></header>
          <div className="share-studio__tabs">
            {(['verdict', 'record', 'truth'] as ShareCardVariant[]).map((item) => (
              <button key={item} className={item === variant ? 'is-active' : ''} onClick={() => setVariant(item)}>{item}</button>
            ))}
          </div>
          <div className="share-studio__preview">
            {preview ? <img src={preview} alt={`${variant} share card preview`} /> : <span>Rendering verified record…</span>}
          </div>
          <div className="share-studio__meta">
            <span>{state.profile.callsign}</span>
            <strong>{state.profile.counselId}</strong>
            <small>Card includes result hash, route and career identity</small>
          </div>
        </div>
      </section>
      {history.length > 1 && (
        <footer className="case-history"><span>PAST RECORDS</span>{history.slice(0, 4).map((item) => <div key={item.resultHash}><strong>{item.rank}</strong><span>{item.verdict}</span><small>{item.resultHash}</small></div>)}</footer>
      )}
    </main>
  );
}
